/* eslint-disable unicorn/no-nested-ternary */

import { DateCell } from '@omnipresentgroup/design-system';
import type { Benefit } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { BenefitCell, BenefitCountry, EmployerCost } from '../components';

export enum BenefitListColumnHeaders {
  employeeName = 'Employee',
  benefit = 'Benefit',
  effectiveDate = 'Effective Date',
  details = 'Details',
  employerCost = 'Employer Cost',
  country = 'Country',
}

export const useBenefitsTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<Benefit, unknown>[];
}) => {
  const columnHelper = useMemo(() => createColumnHelper<Benefit>(), []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Benefit, any>[] = useMemo(
    () => [
      columnHelper.accessor('employeeName', {
        cell: (info) => info.getValue(),
        header: () => BenefitListColumnHeaders.employeeName,
      }),
      columnHelper.accessor('benefitType', {
        cell: (info) => <BenefitCell benefit={info.row.original} />,
        header: () => BenefitListColumnHeaders.benefit,
      }),
      columnHelper.accessor('benefitEffectiveDate', {
        cell: (info) => <DateCell value={info.getValue()} utc />,
        header: () => BenefitListColumnHeaders.effectiveDate,
        sortingFn: 'datetime',
      }),
      columnHelper.accessor('employerCost', {
        cell: (info) => <EmployerCost benefit={info.row.original} />,
        header: () => BenefitListColumnHeaders.employerCost,
      }),
      columnHelper.accessor('country', {
        cell: (info) => <BenefitCountry benefit={info.row.original} />,
        header: () => BenefitListColumnHeaders.country,
        sortingFn: (rowA, rowB) =>
          rowA.original.country?.name > rowB.original.country?.name ? 1 : -1,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
